'use client';

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deliveryApi } from '@/lib/api/delivery';
import { useAddressStore } from '@/stores/address-store';
import { toast } from '@/components/ui/feedback/toast';

const RUB = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

/**
 * Перепроверяет стоимость доставки выбранного адреса при mount компонента.
 *
 * Проблема: address-store persistится в localStorage. Если админ за это время
 * изменил полигон зоны или цену доставки — клиент видит старую цену в корзине,
 * а на чекаут уходит свежая (сервер пересчитал). Без явного предупреждения
 * клиент удивляется на PayKeeper.
 *
 * Решение:
 *   1. При открытии корзины дёргаем checkDeliveryZone() с теми же координатами.
 *   2. Сравниваем с сохранённым snapshot'ом.
 *   3. Если расхождение — обновляем store и показываем тост.
 *
 * Использует TanStack Query со staleTime 30с — чтобы при частом открытии
 * корзины не долбить бэк (защита от перерендеров cart-sheet).
 */
export function useDeliveryRevalidate({ enabled = true }: { enabled?: boolean } = {}) {
  const selectedAddress = useAddressStore((s) => s.selectedAddress);
  const selectedCity = useAddressStore((s) => s.selectedCity);
  const setSelectedAddress = useAddressStore((s) => s.setSelectedAddress);

  const hasAddress = !!selectedAddress?.address;
  const lat = selectedAddress?.coordinates?.lat;
  const lon = selectedAddress?.coordinates?.lon;

  // Ключ запроса учитывает только координаты и адрес — повторные mount'ы
  // в течение 30с используют кэш без повторного запроса.
  const { data: freshResult } = useQuery({
    queryKey: ['delivery-revalidate', selectedAddress?.address, lat, lon, selectedCity?.id],
    queryFn: () =>
      deliveryApi.checkDeliveryZone(
        selectedAddress!.address,
        selectedCity!.id,
        selectedAddress?.coordinates
      ),
    enabled: enabled && hasAddress && !!selectedCity,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  // Защита от повторного тоста на один и тот же diff. Ключ = stringify изменений.
  const lastNotifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!freshResult || !selectedAddress) return;

    // Бэк отдаёт HTTP 200 с полем error, когда не смог определить координаты
    // (геокодер отвалился, дом не подтвердился кросс-чеком). Это не «вне зоны» —
    // снимать с адреса уже посчитанную зону и цену доставки нельзя.
    if (!freshResult.isInZone && freshResult.error) return;

    const oldCost = selectedAddress.deliveryCost ?? null;
    const newCost = freshResult.isInZone ? (freshResult.deliveryCost ?? null) : null;
    const oldInZone = selectedAddress.isInZone;
    const newInZone = freshResult.isInZone;
    const oldMin = selectedAddress.minOrderSum ?? null;
    const newMin = freshResult.isInZone ? (freshResult.minOrderSum ?? null) : null;

    // Ничего не поменялось — не трогаем
    const costChanged = oldCost !== newCost;
    const zoneStatusChanged = oldInZone !== newInZone;
    const minChanged = oldMin !== newMin;
    if (!costChanged && !zoneStatusChanged && !minChanged) return;

    // Защита от двойного тоста при ререндере с теми же данными
    const diffKey = `${oldCost}|${newCost}|${oldInZone}|${newInZone}|${oldMin}|${newMin}`;
    if (lastNotifiedRef.current === diffKey) return;
    lastNotifiedRef.current = diffKey;

    // ── 1. Адрес вышел из зоны доставки ──
    if (oldInZone && !newInZone) {
      const fromZoneId = selectedAddress.deliveryZone?.id ?? null;
      const fromPointId = selectedAddress.deliveryPointId ?? null;
      setSelectedAddress({
        address: selectedAddress.address,
        coordinates: selectedAddress.coordinates,
        isInZone: false,
      });
      toast.warning(
        'Адрес больше не в зоне доставки. Пожалуйста, выберите другой адрес.'
      );
      // Снятие зоны с уже подтверждённого адреса — событие, которого раньше
      // не было видно вообще: человек просто терял доставку из «Итого».
      import('@/lib/source-tracker').then(({ captureEvent }) => {
        captureEvent('zone_downgraded', {
          from_zone_id: fromZoneId,
          point_id: fromPointId,
          reason: 'out_of_zone',
        });
      }).catch(() => { /* ignore */ });
      return;
    }

    // ── 2. Был не в зоне — стал в зоне (расширение полигона) ──
    if (!oldInZone && newInZone) {
      setSelectedAddress({
        address: selectedAddress.address,
        coordinates: selectedAddress.coordinates,
        isInZone: true,
        deliveryPointId: freshResult.deliveryPointId,
        deliveryZone: freshResult.zone,
        deliveryCost: freshResult.deliveryCost,
        estimatedTime: freshResult.estimatedTime,
        minOrderSum: freshResult.minOrderSum,
      });
      toast.success('Адрес теперь в зоне доставки');
      return;
    }

    // ── 3. Изменилась цена доставки или минимальный заказ ──
    if (newInZone) {
      setSelectedAddress({
        address: selectedAddress.address,
        coordinates: selectedAddress.coordinates,
        isInZone: true,
        deliveryPointId: freshResult.deliveryPointId,
        deliveryZone: freshResult.zone,
        deliveryCost: freshResult.deliveryCost,
        estimatedTime: freshResult.estimatedTime,
        minOrderSum: freshResult.minOrderSum,
      });

      if (costChanged && oldCost !== null && newCost !== null) {
        const delta = newCost - oldCost;
        if (delta > 0) {
          toast.info(`Стоимость доставки выросла: ${RUB.format(oldCost)} → ${RUB.format(newCost)}`);
        } else {
          toast.success(`Стоимость доставки снизилась: ${RUB.format(oldCost)} → ${RUB.format(newCost)}`);
        }
      } else if (costChanged && newCost !== null) {
        toast.info(`Стоимость доставки: ${RUB.format(newCost)}`);
      }

      if (minChanged && oldMin !== null && newMin !== null && newMin !== oldMin) {
        toast.info(`Минимальный заказ изменился: ${RUB.format(oldMin)} → ${RUB.format(newMin)}`);
      }
    }
  }, [freshResult, selectedAddress, setSelectedAddress]);
}
